
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Calendar } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { useSundayMarketPrices, useDeleteSundayMarketPrice } from '@/hooks/use-sunday-market-prices';
import { SundayMarketPriceFilters, SundayMarketPrice } from '@/types/database/sunday-market-prices';
import SundayMarketPricesHeader from '@/components/sunday-market/SundayMarketPricesHeader';
import SundayMarketPricesFilter from '@/components/sunday-market/SundayMarketPricesFilter';
import SundayMarketPricesTable from '@/components/sunday-market/SundayMarketPricesTable';
import SundayMarketPriceForm from '@/components/sunday-market/SundayMarketPriceForm';
import SundayMarketUpload from '@/components/sunday-market/upload/SundayMarketUpload';

const SundayMarket: React.FC = () => {
  const [filters, setFilters] = useState<SundayMarketPriceFilters>({});
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [editingPrice, setEditingPrice] = useState<SundayMarketPrice | null>(null);

  const { data: prices = [], isLoading, refetch } = useSundayMarketPrices(filters);
  const deleteSundayMarketPrice = useDeleteSundayMarketPrice();

  const handleFiltersChange = (newFilters: SundayMarketPriceFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({});
  };

  const handleAddNew = () => {
    setEditingPrice(null);
    setShowAddDialog(true);
  };

  const handleUpload = () => {
    setShowUploadDialog(true);
  };

  const handleEdit = (price: SundayMarketPrice) => {
    setEditingPrice(price);
    setShowAddDialog(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this Sunday market price?')) {
      await deleteSundayMarketPrice.mutateAsync(id);
    }
  };

  const handleFormSuccess = () => {
    setShowAddDialog(false);
    setEditingPrice(null);
    refetch();
  };

  const handleUploadSuccess = () => {
    setShowUploadDialog(false);
    refetch();
  };

  return (
    <Layout>
      <div className="container mx-auto p-6">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Calendar className="h-8 w-8 text-orange-600" />
            <h1 className="text-3xl font-bold text-gray-900">Sunday Market Prices</h1>
          </div>
          <p className="text-gray-600">
            Weekend market price tracking
          </p>
        </div>

        <div className="space-y-6">
          <SundayMarketPricesHeader
            onAddNew={handleAddNew}
            onUpload={handleUpload}
          />

          <SundayMarketPricesFilter
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onClearFilters={handleClearFilters}
          />

          <Card>
            <CardHeader>
              <CardTitle>Sunday Market Prices</CardTitle>
            </CardHeader>
            <CardContent>
              <SundayMarketPricesTable
                prices={prices}
                onEdit={handleEdit}
                onDelete={handleDelete}
                isLoading={isLoading}
              />
            </CardContent>
          </Card>
        </div>

        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingPrice ? 'Edit Sunday Market Price' : 'Add New Sunday Market Price'}
              </DialogTitle>
            </DialogHeader>
            <SundayMarketPriceForm
              initialData={editingPrice}
              onSuccess={handleFormSuccess}
              onCancel={() => setShowAddDialog(false)}
            />
          </DialogContent>
        </Dialog>

        {showUploadDialog && (
          <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Upload Sunday Market Prices</DialogTitle>
              </DialogHeader>
              <SundayMarketUpload onSuccess={handleUploadSuccess} />
            </DialogContent>
          </Dialog>
        )}
      </div>
    </Layout>
  );
};

export default SundayMarket;
